import { fetchProductInformations } from '../../utils/service-http';

/**
 * Service managing the description cache
 */
class ProductInformationCache {

  /**
   * The key value description cache
   */
  static descriptionMap = {};

  /**
   * The key value EAN cache
   */
  static eanMap = {}

  /**
   * The key value E-Number cache
   */
  static eNumMap = {}

  /**
   * As the service can't be connected to the store, it has to have a callback which
   * will interact with the store.
   */
  static dispatchFunction;

  /**
   * Set the function which will interact with the store
   * @param {*} dispatchFunction
   */
  static initProductInformationCacheService(dispatchFunction) {
    this.dispatchFunction = dispatchFunction;
  }

  static loadReferencesDescriptions(references) {
    const newReferences = references.filter(ref => !this.isDescriptionCached(ref));

    // Map to keep track of the references from which the description API will return no result.
    const newReferencesMap = {};
    newReferences.forEach(ref => {
      newReferencesMap[ref] = true;
    });

    if (!newReferences.length) return;

    return (async () => {
      newReferences.forEach(refId => {
        this.descriptionMap[refId] = '...';
        this.eanMap[refId] = '...';
        this.eNumMap[refId] = '...';
      });
      const productInformations = await fetchProductInformations(newReferences).catch(e => []);
      const descriptionsToSet = [];
      const eansToSet = [];
      const eNumsToSet = [];
      const referencesWithDescription = [];
      productInformations.forEach(product => {
        referencesWithDescription.push(product.id);
        delete newReferencesMap[product.id];

        descriptionsToSet.push({
          key: product.id,
          desc: product.description
        });
        eansToSet.push({
          key: product.id,
          ean: product.ean,
        });
        eNumsToSet.push({
          key: product.id,
          enum: product.enum,
        });
      });

      // Affect an empty description to the product if it was not returned by the descriptions API (if it was returned, it would have been deleted [see above])
      Object.keys(newReferencesMap).forEach(productKey => {
        descriptionsToSet.push({
          key: productKey,
          desc: ''
        });
      });

      const notFoundDescriptions = newReferences.filter(id => !referencesWithDescription.includes(id));
      notFoundDescriptions.forEach(id => {
        descriptionsToSet.push({
          key: id,
          desc: null
        });
      });

      this.setDescriptions(descriptionsToSet);
      this.setEANs(eansToSet);
      this.setENums(eNumsToSet);
    })();
  }

  /**
   * Get the description from the store.
   * @param {*} key
   * @returns the description
   */
  static getDescription(key) {
    let description = this.descriptionMap[key];
    return description;
  }

  /**
   * Get the EAN from the store.
   * @param {*} key
   * @returns the EAN
   */
  static getEAN(key) {
    let ean = this.eanMap[key];
    return ean;
  }

   /**
   * Get the E-Number from the store.
   * @param {*} key
   * @returns the E-Number
   */
  static getENumber(key) {
    let eNum = this.eNumMap[key];
    return eNum;
  }

  /**
   * Set the description of multiple references to the store.
   * @param {*} keysWithDescription
   */
  static setDescriptions(keysWithDescription) {
    keysWithDescription.forEach((pair) => {
      this.descriptionMap[pair.key] = pair.desc;
    });
    this.dispatchFunction(this.descriptionMap);
  }

  /**
  * Set the EAN of multiple references to the store.
  * @param {*} keysWithEAN
  */
  static setEANs(keysWithEAN) {
    keysWithEAN.forEach((pair) => {
      this.eanMap[pair.key] = pair.ean;
    });
    this.dispatchFunction(this.descriptionMap);
  }

  /**
  * Set the EAN of multiple references to the store.
  * @param {*} keysWithENum
  */
  static setENums(keysWithENum) {
    keysWithENum.forEach((pair) => {
      this.eNumMap[pair.key] = pair.enum;
    });
    this.dispatchFunction(this.descriptionMap);
  }

  /**
   * Detect if it exists a description cached value.
   * @param {*} key
   * @returns true if it exists a description cached value, false if not.
   */
  static isDescriptionCached(key) {
    return this.descriptionMap.hasOwnProperty(key);
  }

  /**
   * Detect if it exists a EAN cached value.
   * @param {*} key
   * @returns true if it exists a EAN cached value, false if not.
   */
  static isEANCached(key) {
    return this.eanMap.hasOwnProperty(key);
  }

  /**
   * Detect if it exists a E-Number cached value.
   * @param {*} key
   * @returns true if it exists a E-Number cached value, false if not.
   */
  static isNumCached(key) {
    return this.eNumMap.hasOwnProperty(key);
  }

  /**
   * Clear the cache.
   */
  static clearCache() {
    this.descriptionMap = {};
    this.eanMap = {};
    this.eNumMap = {};
    this.dispatchFunction(this.descriptionMap);
  }
};

export default ProductInformationCache;
